import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  StoryHistoryArchiveQuery,
  StoryHistoryArchiveRecord,
  StoryHistoryArchiveRecordInput,
  StoryHistoryArchiveState,
} from '@/IsekaiRPG/modules/history/historyTypes';
import {
  StoryHistoryArchiveRecordInputSchema,
  StoryHistoryArchiveStateSchema,
} from '@/IsekaiRPG/modules/history/historyTypes';

function createRecordId(): string {
  return `story_history_archive_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
}

function normalizeRecordInput(input: StoryHistoryArchiveRecordInput): StoryHistoryArchiveRecord {
  const parsed = StoryHistoryArchiveRecordInputSchema.parse(input);
  return {
    ...parsed,
    id: parsed.id || createRecordId(),
    archivedAt: parsed.archivedAt ?? Date.now(),
  };
}

function sortRecords(records: StoryHistoryArchiveRecord[]): StoryHistoryArchiveRecord[] {
  return [...records].sort((left, right) => {
    if (left.endedAt !== right.endedAt) return left.endedAt - right.endedAt;
    if (left.archivedAt !== right.archivedAt) return left.archivedAt - right.archivedAt;
    return left.id.localeCompare(right.id);
  });
}

function applyMaxEntries(records: StoryHistoryArchiveRecord[], maxEntries: number): StoryHistoryArchiveRecord[] {
  if (records.length <= maxEntries) return records;
  return records.slice(records.length - maxEntries);
}

function matchesKeyword(record: StoryHistoryArchiveRecord, keyword: string): boolean {
  if (!keyword) return true;

  return [...record.bridgeSystemEntries, ...record.sessionEntries].some(entry => entry.content.includes(keyword));
}

function containsEntryId(record: StoryHistoryArchiveRecord, entryId: string): boolean {
  if (!entryId) return true;

  return [...record.bridgeSystemEntries, ...record.sessionEntries].some(entry => entry.id === entryId);
}

export const useStoryHistoryArchiveStore = defineStore('storyHistoryArchive', () => {
  const state = ref<StoryHistoryArchiveState>(
    StoryHistoryArchiveStateSchema.parse({
      records: [],
    }),
  );

  const records = computed(() => state.value.records);

  function setMaxEntries(maxEntries: number): void {
    state.value.maxEntries = Math.max(1, Math.trunc(maxEntries));
    state.value.records = applyMaxEntries(sortRecords(state.value.records), state.value.maxEntries);
  }

  function replaceRecords(nextRecords: StoryHistoryArchiveRecordInput[]): void {
    const normalized = nextRecords.map(record => normalizeRecordInput(record));
    state.value.records = applyMaxEntries(sortRecords(normalized), state.value.maxEntries);
  }

  function appendRecord(input: StoryHistoryArchiveRecordInput): StoryHistoryArchiveRecord {
    const created = normalizeRecordInput(input);
    state.value.records = applyMaxEntries(sortRecords([...state.value.records, created]), state.value.maxEntries);
    return created;
  }

  function appendRecords(inputs: StoryHistoryArchiveRecordInput[]): StoryHistoryArchiveRecord[] {
    const created = inputs.map(input => normalizeRecordInput(input));
    state.value.records = applyMaxEntries(sortRecords([...state.value.records, ...created]), state.value.maxEntries);
    return created;
  }

  function getRecord(recordId: string): StoryHistoryArchiveRecord | null {
    return state.value.records.find(record => record.id === recordId) ?? null;
  }

  function removeRecord(recordId: string): boolean {
    const before = state.value.records.length;
    state.value.records = state.value.records.filter(record => record.id !== recordId);
    return state.value.records.length !== before;
  }

  function clearRecords(): void {
    state.value.records = [];
  }

  function queryRecords(query: StoryHistoryArchiveQuery = {}): StoryHistoryArchiveRecord[] {
    const { sourceSummaryEntryId, sessionKey, sceneName, participantId, entryId, keyword, limit } = query;

    let result = state.value.records.filter(record => {
      if (sourceSummaryEntryId && record.sourceSummaryEntryId !== sourceSummaryEntryId) return false;
      if (sessionKey && record.sessionKey !== sessionKey) return false;
      if (sceneName && record.sceneName !== sceneName) return false;
      if (participantId && !record.participantIds.includes(participantId)) return false;
      if (!containsEntryId(record, entryId || '')) return false;
      if (!matchesKeyword(record, keyword || '')) return false;
      return true;
    });

    result = sortRecords(result);
    if (limit && result.length > limit) {
      result = result.slice(result.length - limit);
    }

    return result;
  }

  return {
    state,
    records,
    setMaxEntries,
    replaceRecords,
    appendRecord,
    appendRecords,
    getRecord,
    removeRecord,
    clearRecords,
    queryRecords,
  };
});
